import React from "react";
import PropTypes from "prop-types";
// Material imports
import makeStyles from "@mui/styles/makeStyles";
import TableFooter from "@mui/material/TableFooter";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
//
import { HeaderIconButton } from "@limitless/portal";
import { getPageState, canGoToPage, parsePageSize } from "./paginationUtils";
const useStyles = makeStyles({
  fetchRowsText: {
    marginRight: 8,
    marginBottom: 4
  },
  fetchRowsSelector: {
    marginRight: 4
  },
  footerRow: {
    display: "flex",
    justifyContent: "flex-end"
  },
  footerCell: {
    display: "flex",
    alignItems: "center"
  },
  pageText: {
    margin: "0 8px",
    whiteSpace: "nowrap"
  },
  pageSizeLabel: {
    marginRight: 8,
    whiteSpace: "nowrap"
  },
  pageSizeInput: {
    // Sized for the 3-digit max (250) plus the number spinner; keeps the underline from running long.
    width: 44,
    marginRight: 16
  }
});

// LD-15706 — offset page navigation for the CM Onboarding screens (PQs & Checks / Outcomes / Brand Learning).
// Rendered when `paged` is true; `page` is 0-based and `count` is the server total for the current filters.
// The non-paged branch (fetch-limit dropdown + keyset "load more") is unchanged for every other consumer.
const PagedControls = ({
  page = 0,
  pageSize = 50,
  maxPageSize = 250,
  count = 0,
  loading = false,
  onPageChange,
  onPageSizeChange
}) => {
  const classes = useStyles();
  const {
    totalPages,
    currentPage,
    isFirst,
    isLast
  } = getPageState(page, pageSize, count);
  const goTo = target => {
    if (canGoToPage(target, currentPage, totalPages, loading)) {
      onPageChange(target);
    }
  };

  // Local draft so typing doesn't refetch on every keystroke; commit (clamped to 1..maxPageSize) on blur/Enter.
  const [draftSize, setDraftSize] = React.useState(pageSize);
  React.useEffect(() => {
    setDraftSize(pageSize);
  }, [pageSize]);
  const commitPageSize = () => {
    const next = parsePageSize(draftSize, maxPageSize);
    if (next == null) {
      // Blank / non-numeric → revert to the current size rather than commit 0.
      setDraftSize(pageSize);
      return;
    }
    setDraftSize(next);
    if (next !== pageSize) {
      onPageSizeChange(next);
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, onPageSizeChange && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    className: classes.pageSizeLabel,
    color: "textPrimary"
  }, "Rows per page:"), /*#__PURE__*/React.createElement(TextField, {
    type: "number",
    size: "small",
    variant: "standard",
    disabled: loading,
    value: draftSize,
    onChange: event => setDraftSize(event.target.value),
    onBlur: commitPageSize,
    onKeyDown: event => {
      if (event.key === "Enter") {
        commitPageSize();
      }
    },
    className: classes.pageSizeInput,
    inputProps: {
      min: 1,
      max: maxPageSize,
      id: "paginationPageSize",
      "aria-label": "Rows per page",
      style: {
        textAlign: "center"
      }
    }
  })), /*#__PURE__*/React.createElement(HeaderIconButton, {
    disabled: loading || isFirst,
    onButtonClick: () => goTo(0),
    tooltip: "First page",
    buttonId: "paginationFirstPage",
    icon: /*#__PURE__*/React.createElement(FirstPageIcon, null)
  }), /*#__PURE__*/React.createElement(HeaderIconButton, {
    disabled: loading || isFirst,
    onButtonClick: () => goTo(currentPage - 1),
    tooltip: "Previous page",
    buttonId: "paginationPrevPage",
    icon: /*#__PURE__*/React.createElement(KeyboardArrowLeftIcon, null)
  }), /*#__PURE__*/React.createElement(Typography, {
    className: classes.pageText,
    color: "textPrimary"
  }, "Page ", currentPage + 1, " of ", totalPages, " (", count, " ", count === 1 ? "record" : "records", ")"), loading ? /*#__PURE__*/React.createElement(CircularProgress, {
    size: 24
  }) : /*#__PURE__*/React.createElement(HeaderIconButton, {
    disabled: isLast,
    onButtonClick: () => goTo(currentPage + 1),
    tooltip: "Next page",
    buttonId: "paginationNextPage",
    icon: /*#__PURE__*/React.createElement(KeyboardArrowRightIcon, null)
  }), /*#__PURE__*/React.createElement(HeaderIconButton, {
    disabled: loading || isLast,
    onButtonClick: () => goTo(totalPages - 1),
    tooltip: "Last page",
    buttonId: "paginationLastPage",
    icon: /*#__PURE__*/React.createElement(LastPageIcon, null)
  }));
};
PagedControls.propTypes = {
  page: PropTypes.number,
  pageSize: PropTypes.number,
  maxPageSize: PropTypes.number,
  count: PropTypes.number,
  loading: PropTypes.bool,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func
};
const DataTableFooter = ({
  isLoadMoreDisabled = true,
  limit,
  fetchRowOptions = [10, 50, 100, 1000],
  changeRowLimit,
  onLoadMore,
  loading = false,
  paged = false,
  page = 0,
  pageSize = 50,
  maxPageSize = 250,
  count = 0,
  onPageChange,
  onPageSizeChange
}) => {
  const classes = useStyles();
  const handleRowChange = event => {
    changeRowLimit(event.target.value);
  };
  const handleLoadMore = () => {
    onLoadMore(null, true);
  };
  return /*#__PURE__*/React.createElement(TableFooter, null, /*#__PURE__*/React.createElement(TableRow, {
    className: classes.footerRow
  }, /*#__PURE__*/React.createElement(TableCell, {
    className: classes.footerCell
  }, paged ? /*#__PURE__*/React.createElement(PagedControls, {
    page: page,
    pageSize: pageSize,
    maxPageSize: maxPageSize,
    count: count,
    loading: loading,
    onPageChange: onPageChange,
    onPageSizeChange: onPageSizeChange
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    className: classes.fetchRowsText,
    color: "textPrimary"
  }, "Fetch data limit:"), /*#__PURE__*/React.createElement(Select, {
    disableUnderline: true,
    className: classes.fetchRowsSelector,
    autoWidth: true,
    id: "fetchRowsAutoComplete",
    value: limit,
    onChange: handleRowChange,
    variant: "standard"
  }, fetchRowOptions.length > 0 && fetchRowOptions.map(num => /*#__PURE__*/React.createElement(MenuItem, {
    key: num,
    value: num
  }, num))), loading ? /*#__PURE__*/React.createElement(CircularProgress, {
    size: 24
  }) : /*#__PURE__*/React.createElement(HeaderIconButton, {
    disabled: isLoadMoreDisabled,
    onButtonClick: handleLoadMore,
    tooltip: "Fetch more data",
    buttonId: `fetchMoreData${Math.floor(Math.random() * 100)}`,
    icon: /*#__PURE__*/React.createElement(ImportExportIcon, null)
  })))));
};
export default DataTableFooter;
DataTableFooter.propTypes = {
  loading: PropTypes.bool,
  isLoadMoreDisabled: PropTypes.bool,
  fetchRowOptions: PropTypes.array,
  limit: PropTypes.number,
  changeRowLimit: PropTypes.func,
  onLoadMore: PropTypes.func,
  paged: PropTypes.bool,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  maxPageSize: PropTypes.number,
  count: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func
};